import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { PaymentPlanTemplateModal } from '@/components/settings/payment-plan-template-modal';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';

type Template = {
  id: string;
  name: string;
  description: string | null;
  depositType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  depositValue: number;
  installments: Array<{
    name: string;
    percentage?: number;
    fixedAmount?: number;
    daysBeforeTravel: number;
  }>;
  balanceDueDaysBeforeTravel: number | null;
  isDefault: boolean;
  isActive: boolean;
};

export function PaymentPlanTemplatesPage() {
  const { t } = useTranslation('paymentPlans');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const deleteDialog = useConfirmationDialog();

  const { data: templates, isLoading, refetch } = trpc.paymentPlanTemplate.list.useQuery({
    activeOnly: false,
  });

  const utils = trpc.useUtils();

  const deleteMutation = trpc.paymentPlanTemplate.delete.useMutation({
    onSuccess: () => {
      utils.paymentPlanTemplate.list.invalidate();
      showSuccessToast(t('templates.successDeleted'));
    },
    onError: (error) => {
      showErrorToast(error.message || t('templates.failedDelete'));
    },
  });

  const setDefaultMutation = trpc.paymentPlanTemplate.setDefault.useMutation({
    onSuccess: () => {
      utils.paymentPlanTemplate.list.invalidate();
      showSuccessToast(t('templates.successUpdated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteDialog.confirm(
      {
        title: t('templates.deleteTemplate'),
        description: t('templates.confirmDelete'),
        confirmLabel: t('templates.deleteTemplate'),
        variant: 'destructive',
      },
      () => {
        deleteMutation.mutate(id);
      }
    );
  };

  const handleSetDefault = (id: string) => {
    setDefaultMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('templates.title')}</h1>
          <p className="text-muted-foreground">{t('templates.subtitle')}</p>
        </div>
        <Button className="h-8" onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          {t('templates.newTemplate')}
        </Button>
      </div>

      {/* Templates List */}
      {templates && templates.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`border border-border bg-card p-4 ${
                !template.isActive ? 'opacity-60' : ''
              }`}
            >
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">{template.name}</h3>
                    {template.isDefault && (
                      <Badge variant="default" className="gap-1 px-1.5 py-0">
                        <Star className="h-3 w-3" />
                        {t('templates.isDefault')}
                      </Badge>
                    )}
                  </div>
                  {template.description && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {template.description}
                    </p>
                  )}
                </div>
                {!template.isActive && (
                  <Badge variant="secondary" className="px-1.5 py-0">Inactive</Badge>
                )}
              </div>

              {/* Deposit Info */}
              <div className="mb-3 bg-muted/50 p-2">
                <p className="text-xs font-medium">Deposit</p>
                <p className="text-sm font-semibold">
                  {template.depositType === 'PERCENTAGE'
                    ? `${template.depositValue}%`
                    : formatCurrency(template.depositValue, 'TRY')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(`depositTypes.${template.depositType}`)}
                </p>
              </div>

              {/* Installments Summary */}
              <div className="mb-3">
                <p className="mb-1 text-xs font-medium">{t('templates.installments')}</p>
                {template.installments.length > 0 ? (
                  <div className="space-y-0.5">
                    {template.installments.map((inst, index) => (
                      <div
                        key={index}
                        className="flex justify-between text-xs"
                      >
                        <span className="text-muted-foreground">{inst.name}</span>
                        <span>
                          {inst.percentage !== undefined
                            ? `${inst.percentage}%`
                            : inst.fixedAmount !== undefined
                            ? formatCurrency(inst.fixedAmount, 'TRY')
                            : 'Remaining'}
                          {' - '}
                          {inst.daysBeforeTravel} {t('common.days')} {t('common.beforeTravel')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : template.balanceDueDaysBeforeTravel !== null ? (
                  <p className="text-xs text-muted-foreground">
                    Balance due {template.balanceDueDaysBeforeTravel} {t('common.days')}{' '}
                    {t('common.beforeTravel')}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Full payment on booking
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 border-t border-border pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 flex-1"
                  onClick={() => handleEdit(template)}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  {t('templates.editTemplate')}
                </Button>
                {!template.isDefault && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => handleSetDefault(template.id)}
                      title="Set as default"
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => handleDelete(template.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-border bg-card p-12 text-center">
          <p className="text-sm text-muted-foreground">{t('templates.noTemplates')}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('templates.createFirst')}
          </p>
          <Button className="mt-4 h-8" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t('templates.newTemplate')}
          </Button>
        </div>
      )}

      {/* Template Modal */}
      <PaymentPlanTemplateModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        template={editingTemplate}
        onSuccess={() => {
          refetch();
          setModalOpen(false);
          setEditingTemplate(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      {deleteDialog.Dialog}
    </div>
  );
}
